import { useEffect, useRef, useState } from 'react';
import { useOnboardingStatus } from './useOnboardingStatus';
import { ESSENTIAL_STEP_KEYS } from './onboardingSteps';

/**
 * ¿Acaba de completarse AHORA la tanda esencial del onboarding?
 *
 * Detecta la TRANSICIÓN, no el estado: celebrar "está completo" dispararía el
 * confeti en cada carga de una empresa configurada hace meses.
 *
 * Vive fuera de `Layout` por una razón concreta: la regla tiene dos guardas
 * sutiles que ya fallaron una vez cada una, y dentro de un componente de 700
 * líneas no había forma de fijarlas con un test.
 *
 *   1. **Tri-estado.** `null` mientras la query no resuelve. Sin él, el ref
 *      arrancaba en `false` en toda carga en frío y la primera resolución con lo
 *      esencial ya hecho parecía una transición real → confeti en cada F5.
 *
 *   2. **Reseteo por empresa.** La `queryKey` lleva `company?.id`, así que al
 *      cambiar de empresa el estado vuelve a `undefined` → el efecto sale antes
 *      de tocar el ref, que se quedaba con el valor de la empresa ANTERIOR. Si
 *      esa estaba a medias y la nueva ya está configurada, se leía como
 *      transición y celebraba una empresa que no acababa de completar nada.
 *
 * @param {string|undefined} companyId empresa activa
 * @returns {{ celebrating: boolean, dismiss: () => void }}
 */
export function useEssentialCelebration(companyId) {
    const { status } = useOnboardingStatus();

    const essentialComplete = status
        ? ESSENTIAL_STEP_KEYS.every((k) => status.steps?.find((s) => s.key === k)?.completed)
        : null;

    const prevRef = useRef(null);
    const [celebrating, setCelebrating] = useState(false);

    // Guarda 2: volver a empezar con cada empresa.
    useEffect(() => {
        prevRef.current = null;
        setCelebrating(false);
    }, [companyId]);

    useEffect(() => {
        if (essentialComplete === null) return;   // guarda 1
        if (prevRef.current === false && essentialComplete) setCelebrating(true);
        prevRef.current = essentialComplete;
    }, [essentialComplete]);

    return { celebrating, dismiss: () => setCelebrating(false) };
}

export default useEssentialCelebration;
